"use client";

/**
 * Client-side provider + hooks. Hydrated from the SSR snapshot so we never
 * render an empty state on first paint and we never cause hydration mismatches
 * (the server already evaluated every flag with the user's audiences).
 *
 * Usage:
 *   <FlagsProvider snapshot={snapshot} audiences={aud}>{children}</FlagsProvider>
 *   useFlag("rufus.navMenuEntry")     // boolean
 *   useFlag("products.deals")          // respects parent section
 *   useFlags()                         // full snapshot
 *   useAudiences()                     // readonly string[]
 *   useHasAudience("admins")           // boolean
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { FlagsSnapshot } from "./types";

interface FlagsContextValue {
  snapshot: FlagsSnapshot;
  audiences: readonly string[];
}

const FlagsContext = createContext<FlagsContextValue | null>(null);

export function FlagsProvider({
  snapshot,
  audiences = [],
  children,
}: {
  snapshot: FlagsSnapshot;
  audiences?: readonly string[];
  children: ReactNode;
}) {
  // The snapshot is server-rendered and immutable for the lifetime of the
  // page, so a single reference-equality memo is enough — no deep checks.
  const value = useMemo<FlagsContextValue>(
    () => ({ snapshot, audiences }),
    [snapshot, audiences]
  );
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

const EMPTY_SNAPSHOT: FlagsSnapshot = Object.freeze({});
const EMPTY_AUDIENCES: readonly string[] = Object.freeze([]);

/**
 * Read the entire snapshot. Returns an empty record if the provider isn't
 * mounted (e.g. tests, isolated stories) — calls then degrade to "everything
 * disabled" rather than crashing.
 */
export function useFlags(): FlagsSnapshot {
  const ctx = useContext(FlagsContext);
  return ctx?.snapshot ?? EMPTY_SNAPSHOT;
}

/**
 * Read a single flag. Sub-features (`section.feature`) are automatically
 * gated by their parent — if the section is off, the sub-feature is off
 * regardless of its own value. This mirrors the server evaluator.
 */
export function useFlag(key: string): boolean {
  const flags = useFlags();
  const dot = key.indexOf(".");
  if (dot > 0) {
    const parent = key.slice(0, dot);
    if (parent in flags && flags[parent] === false) return false;
  }
  return flags[key] === true;
}

/** Convenience: section gate. */
export function useSection(section: string): boolean {
  return useFlag(section);
}

/**
 * Renders children only when the flag is on. It is a client boundary, so
 * markup shared by server and client trees can gate itself without hooks.
 */
export function ClientFlagGate({ flag, children }: { flag: string; children: ReactNode }) {
  return useFlag(flag) ? <>{children}</> : null;
}

/** Active audience tokens for the current request. */
export function useAudiences(): readonly string[] {
  const ctx = useContext(FlagsContext);
  return ctx?.audiences ?? EMPTY_AUDIENCES;
}

/** Check whether the active audience set contains a specific token. */
export function useHasAudience(token: string): boolean {
  const audiences = useAudiences();
  return audiences.includes(token);
}
